import { useState } from 'react';
import { Link } from 'react-router-dom';
import { allServices } from '@/mocks/service-details';
import { testimonialsData } from '@/mocks/home';
import { getWhatsAppUrl } from '@/utils/whatsapp';
import { useScrollAnimation, useCountUp } from '@/hooks/useScrollAnimation';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';
import JsonLd, { getServiceSchema, getFAQPageSchema, getWebPageSchema, getBreadcrumbSchema } from '@/utils/seo';

interface ServiceFeature {
  icon: string;
  title: string;
  description: string;
}

interface ProcessStep {
  step: string;
  title: string;
  description: string;
}

interface FAQItem {
  question: string;
  answer: string;
}

interface ServiceDetailData {
  slug: string;
  title: string;
  tagline: string;
  heroImage: string;
  overview: string;
  detailedDescription?: string;
  highlights: { value: string; label: string }[];
  features: ServiceFeature[];
  gallery: string[];
  process: ProcessStep[];
  faqs?: FAQItem[];
  ctaTitle: string;
  ctaText: string;
}

const whyChooseUsItems = [
  { icon: 'ri-user-star-line', title: 'Experienced Professionals' },
  { icon: 'ri-award-line', title: 'Quality Materials' },
  { icon: 'ri-price-tag-3-line', title: 'Affordable Pricing' },
  { icon: 'ri-shield-check-line', title: 'Reliable Service' },
  { icon: 'ri-time-line', title: 'Timely Delivery' },
  { icon: 'ri-team-line', title: 'Professional Team' },
];

function HighlightCounter({ value, label, isVisible }: { value: number; label: string; isVisible: boolean }) {
  const count = useCountUp(value, 1800, isVisible);
  return (
    <div className="text-center">
      <div className="font-heading font-bold text-xl md:text-2xl lg:text-3xl text-primary-500">
        {count}+
      </div>
      <div className="text-xs md:text-sm text-foreground-500 mt-1">{label}</div>
    </div>
  );
}

export default function ServiceDetailLayout({ data }: { data: ServiceDetailData }) {
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const currentServiceSlug = data.slug;
  const relatedServices = allServices.filter((s) => s.slug !== currentServiceSlug);

  useBodyScrollLock(lightboxImage !== null);

  const relevantTestimonials = testimonialsData.slice(0, 3);

  const { ref: highlightsRef, isVisible: highlightsVisible } = useScrollAnimation(0.1);
  const { ref: overviewRef, isVisible: overviewVisible } = useScrollAnimation(0.05);
  const { ref: featuresRef, isVisible: featuresVisible } = useScrollAnimation(0.03);
  const { ref: processRef, isVisible: processVisible } = useScrollAnimation(0.05);
  const { ref: whyUsRef, isVisible: whyUsVisible } = useScrollAnimation(0.05);
  const { ref: galleryRef, isVisible: galleryVisible } = useScrollAnimation(0.03);
  const { ref: reviewsRef, isVisible: reviewsVisible } = useScrollAnimation(0.05);
  const { ref: faqRef, isVisible: faqVisible } = useScrollAnimation(0.05);
  const { ref: relatedRef, isVisible: relatedVisible } = useScrollAnimation(0.05);

  return (
    <div className="bg-white">
      <JsonLd data={getServiceSchema(data.title, data.overview.substring(0, 200), `https://infiniteconsultant.com/services/${data.slug}`, data.heroImage)} />
      {data.faqs && data.faqs.length > 0 && <JsonLd data={getFAQPageSchema(data.faqs)} />}
      <JsonLd data={getWebPageSchema(`${data.title} | Infinite Consultant & Services`, data.tagline, `https://infiniteconsultant.com/services/${data.slug}`)} />
      <JsonLd data={getBreadcrumbSchema([{ name: 'Home', url: 'https://infiniteconsultant.com/' }, { name: 'Services', url: 'https://infiniteconsultant.com/services' }, { name: data.title, url: `https://infiniteconsultant.com/services/${data.slug}` }])} />
      {/* Hero Section */}
      <section className="relative h-[420px] md:h-[520px] w-full overflow-hidden">
        <img
          src={data.heroImage}
          alt={data.title}
          className="absolute inset-0 w-full h-full object-cover object-top animate-reveal"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60" />

        <div className="absolute inset-0 flex items-end">
          <div className="w-full px-4 sm:px-6 lg:px-10 pb-10 md:pb-14">
            <div className="max-w-6xl mx-auto">
              <nav className="flex items-center gap-2 text-white/80 text-sm mb-4" aria-label="Breadcrumb">
                <Link to="/" className="hover:text-white transition-colors whitespace-nowrap cursor-pointer">Home</Link>
                <span className="text-white/50">/</span>
                <Link to="/services" className="hover:text-white transition-colors whitespace-nowrap cursor-pointer">Services</Link>
                <span className="text-white/50">/</span>
                <span className="text-white whitespace-nowrap">{data.title}</span>
              </nav>
              <h1 className="font-heading font-bold text-2xl md:text-4xl lg:text-5xl text-white mb-3 max-w-3xl" style={{ opacity: 0, animation: 'fadeUpSoft 0.7s cubic-bezier(0.16, 1, 0.3, 1) 0.1s forwards' }}>
                {data.title}
              </h1>
              <p className="text-sm md:text-lg text-white/80 max-w-2xl mb-6" style={{ opacity: 0, animation: 'fadeUpSoft 0.7s cubic-bezier(0.16, 1, 0.3, 1) 0.25s forwards' }}>
                {data.tagline}
              </p>
              <div className="flex flex-wrap gap-3" style={{ opacity: 0, animation: 'fadeUpSoft 0.7s cubic-bezier(0.16, 1, 0.3, 1) 0.4s forwards' }}>
                <a
                  href={getWhatsAppUrl(data.title)}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="btn-ripple inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-secondary-500 text-white font-medium text-sm hover:bg-secondary-600 transition-all duration-300 hover:scale-[1.03] whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-line" />
                  Enquire on WhatsApp
                </a>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-white/10 text-white border border-white/20 font-medium text-sm hover:bg-white/20 transition-all duration-300 hover:scale-[1.03] whitespace-nowrap cursor-pointer"
                >
                  Get Free Quotation
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Highlights Bar */}
      <section ref={highlightsRef} className={`bg-background-50 border-b border-background-200/70 transition-all duration-700 ${highlightsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div className="max-w-6xl mx-auto py-6 md:py-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
              {data.highlights.map((h, i) => {
                const numericValue = parseInt(h.value) || 0;
                if (numericValue > 0) {
                  return <HighlightCounter key={i} value={numericValue} label={h.label} isVisible={highlightsVisible} />;
                }
                return (
                  <div key={i} className="text-center">
                    <div className="font-heading font-bold text-xl md:text-2xl lg:text-3xl text-primary-500">
                      {h.value}
                    </div>
                    <div className="text-xs md:text-sm text-foreground-500 mt-1">{h.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section ref={overviewRef} className={`py-12 md:py-20 transition-all duration-700 ${overviewVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div className="max-w-6xl mx-auto">
            <div className="max-w-3xl">
              <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-5">Overview</h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">{data.overview}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Description */}
      {data.detailedDescription && (
        <section className="py-12 md:py-20 bg-background-100">
          <div className="w-full px-4 sm:px-6 lg:px-10">
            <div className="max-w-6xl mx-auto">
              <div className="max-w-3xl">
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-5">How We Deliver Excellence</h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">{data.detailedDescription}</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Features Grid — Key Benefits */}
      <section ref={featuresRef} className="py-12 md:py-20 bg-background-50">
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-2">What We Offer</h2>
            <p className="text-sm text-foreground-500 mb-8 md:mb-10">Specialized services tailored to deliver exceptional results.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {data.features.map((feature, i) => (
                <div
                  key={i}
                  className={`bg-white rounded-xl p-5 md:p-6 border border-background-200/70 hover:shadow-sm hover:-translate-y-0.5 transition-all duration-500 ${featuresVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                  style={{ transitionDelay: `${i * 0.08}s` }}
                >
                  <div className="w-10 h-10 rounded-lg bg-accent-50 flex items-center justify-center mb-4">
                    <i className={`${feature.icon} text-accent-600 text-lg`} />
                  </div>
                  <h3 className="font-heading font-semibold text-sm md:text-base text-foreground-950 mb-2">{feature.title}</h3>
                  <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section ref={processRef} className={`py-12 md:py-20 transition-all duration-700 ${processVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-2">Our Process</h2>
            <p className="text-sm text-foreground-500 mb-8 md:mb-10">How we deliver excellence, step by step.</p>
            <div className="relative">
              <div className="hidden md:block absolute top-8 left-0 right-0 h-0.5 bg-background-200" />
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 md:gap-6">
                {data.process.map((step, i) => (
                  <div key={i} className="relative flex md:flex-col items-start md:items-center gap-4 md:gap-0 md:text-center">
                    <div className="w-10 h-10 md:w-14 md:h-14 rounded-full bg-primary-500 text-white flex items-center justify-center shrink-0 z-10 transition-transform duration-300 hover:scale-110">
                      <span className="font-heading font-bold text-sm md:text-base">{step.step}</span>
                    </div>
                    <div className="md:mt-3 md:px-2">
                      <div className="font-heading font-semibold text-sm md:text-base text-foreground-950 mb-1">{step.title}</div>
                      <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section ref={whyUsRef} className="py-12 md:py-20 bg-background-50">
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-2 text-center">Why Choose Us</h2>
            <p className="text-sm text-foreground-500 mb-8 md:mb-10 text-center max-w-xl mx-auto">What makes us the preferred choice for your {data.title.toLowerCase()} needs.</p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
              {whyChooseUsItems.map((item, i) => (
                <div
                  key={i}
                  className={`bg-white rounded-xl p-4 md:p-5 border border-background-200/70 text-center hover:shadow-sm transition-all duration-500 hover:-translate-y-1 ${whyUsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                  style={{ transitionDelay: `${i * 0.06}s` }}
                >
                  <div className="w-10 h-10 mx-auto mb-2 rounded-lg bg-gradient-brand flex items-center justify-center">
                    <i className={`${item.icon} text-white text-base`} />
                  </div>
                  <h4 className="font-heading font-medium text-xs text-foreground-950">{item.title}</h4>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Photo Gallery */}
      <section ref={galleryRef} className="py-12 md:py-20">
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-2">Project Gallery</h2>
            <p className="text-sm text-foreground-500 mb-8 md:mb-10">Browse through our recent work.</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
              {data.gallery.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setLightboxImage(img)}
                  className={`relative overflow-hidden rounded-xl bg-background-100 group cursor-pointer transition-all duration-500 ${galleryVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
                  style={{ transitionDelay: `${i * 0.1}s` }}
                >
                  <div className="aspect-[4/3] md:aspect-auto md:h-full">
                    <img
                      src={img}
                      alt={`${data.title} project ${i + 1}`}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                  </div>
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center">
                        <i className="ri-zoom-in-line text-foreground-700 text-lg" />
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Customer Reviews */}
      <section ref={reviewsRef} className="py-12 md:py-20 bg-background-50">
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-2 text-center">Customer Reviews</h2>
            <p className="text-sm text-foreground-500 mb-8 md:mb-10 text-center max-w-xl mx-auto">Hear from our happy clients.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {relevantTestimonials.map((t, i) => (
                <div
                  key={t.id}
                  className={`bg-white rounded-xl p-5 md:p-6 border border-background-200/70 hover:shadow-sm transition-all duration-500 ${reviewsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                  style={{ transitionDelay: `${i * 0.1}s` }}
                >
                  <div className="flex items-center gap-0.5 mb-3">
                    {[...Array(t.rating)].map((_, idx) => (
                      <div key={idx} className="w-4 h-4 flex items-center justify-center text-accent-500">
                        <i className="ri-star-fill text-xs" />
                      </div>
                    ))}
                  </div>
                  <blockquote className="text-sm text-foreground-700 leading-relaxed mb-4 italic">
                    &ldquo;{t.text}&rdquo;
                  </blockquote>
                  <div className="flex items-center gap-3 pt-3 border-t border-background-100">
                    <div className="w-10 h-10 rounded-full overflow-hidden shrink-0">
                      <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <div className="font-heading font-semibold text-xs text-foreground-950">{t.name}</div>
                      <div className="text-xs text-foreground-500">{t.location}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      {data.faqs && data.faqs.length > 0 && (
        <section ref={faqRef} className={`py-12 md:py-20 transition-all duration-700 ${faqVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="w-full px-4 sm:px-6 lg:px-10">
            <div className="max-w-3xl mx-auto">
              <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-2 text-center">Frequently Asked Questions</h2>
              <p className="text-sm text-foreground-500 mb-8 md:mb-10 text-center">Quick answers to common questions about our {data.title.toLowerCase()} service.</p>
              <div className="space-y-3">
                {data.faqs.map((faq, i) => (
                  <div
                    key={i}
                    className={`bg-white rounded-xl border transition-all duration-300 ${
                      openFaq === i ? 'border-primary-200/70 shadow-sm' : 'border-background-200/70'
                    }`}
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === i ? null : i)}
                      className="w-full flex items-center justify-between p-4 md:p-5 text-left cursor-pointer"
                    >
                      <span className="font-heading font-medium text-sm md:text-base text-foreground-950 pr-4">
                        {faq.question}
                      </span>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-all duration-300 ${
                        openFaq === i ? 'bg-primary-500 text-white rotate-180' : 'bg-background-100 text-foreground-600'
                      }`}>
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-arrow-down-s-line text-lg" />
                        </div>
                      </div>
                    </button>
                    <div
                      className={`overflow-hidden transition-all duration-300 ${
                        openFaq === i ? 'max-h-96' : 'max-h-0'
                      }`}
                    >
                      <div className="px-4 md:px-5 pb-4 md:pb-5">
                        <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-14 md:py-20 bg-gradient-brand">
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-white mb-4">{data.ctaTitle}</h2>
            <p className="text-sm md:text-base text-white/80 max-w-xl mx-auto mb-8">{data.ctaText}</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/contact"
                className="btn-ripple inline-flex items-center gap-2 px-6 py-3 bg-white text-primary-600 rounded-lg font-semibold text-sm hover:bg-background-50 transition-all duration-300 hover:scale-[1.03] whitespace-nowrap cursor-pointer"
              >
                Get Free Quotation
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-base" />
                </div>
              </Link>
              <a
                href={getWhatsAppUrl(data.title)}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 text-white border border-white/20 rounded-lg font-medium text-sm hover:bg-white/20 transition-all duration-300 hover:scale-[1.03] whitespace-nowrap cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-whatsapp-line text-base" />
                </div>
                WhatsApp Now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section ref={relatedRef} className="py-12 md:py-20">
        <div className="w-full px-4 sm:px-6 lg:px-10">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-2">Explore Other Services</h2>
            <p className="text-sm text-foreground-500 mb-8 md:mb-10">Discover more ways we can help transform your property.</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
              {relatedServices.map((svc, i) => (
                <Link
                  key={svc.slug}
                  to={`/services/${svc.slug}`}
                  className={`bg-background-50 rounded-xl p-5 border border-background-200/70 hover:bg-white hover:shadow-sm hover:-translate-y-0.5 transition-all duration-500 group whitespace-nowrap cursor-pointer ${relatedVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                  style={{ transitionDelay: `${i * 0.08}s` }}
                >
                  <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center mb-3 group-hover:bg-primary-100 transition-colors">
                    <i className={`${svc.icon} text-primary-500 text-lg`} />
                  </div>
                  <h3 className="font-heading font-semibold text-sm text-foreground-950 leading-tight">{svc.title}</h3>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 cursor-pointer animate-fade-in"
          onClick={() => setLightboxImage(null)}
        >
          <button
            onClick={() => setLightboxImage(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors cursor-pointer"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-close-line text-xl" />
            </div>
          </button>
          <img
            src={lightboxImage}
            alt="Gallery preview"
            className="max-w-full max-h-[90vh] rounded-xl object-contain animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}